import {attr, controller, target, targets} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller
export class LfsCalculatorElement extends HTMLElement {
  @targets plans: HTMLInputElement[]
  get selectedPlan() {
    return this.plans.find(planOption => planOption.checked)
  }
  get plan() {
    return (this.selectedPlan && this.selectedPlan.value) || ''
  }
  get planLabel() {
    return (this.selectedPlan && this.selectedPlan.getAttribute('data-label')) || ''
  }
  get includedAmount() {
    return this.unit_data?.hasOwnProperty(this.plan) ? this.unit_data[this.plan]!['value'] : 0
  }

  @targets planLabels: HTMLElement[]

  @target bandwidthInput: HTMLInputElement
  get bandwidth() {
    return parseInt(this.bandwidthInput.value) || 0
  }

  @target storageInput: HTMLInputElement
  get storage() {
    return parseInt(this.storageInput.value) || 0
  }

  @target subtotalElement: HTMLElement
  @target includedAmountElement: HTMLElement

  @attr costPerMonth = 0
  @attr active = false

  unit_data: UnitData

  connectedCallback() {
    this.unit_data = getUnitData()
  }

  displayCostPerMonth() {
    this.subtotalElement.textContent = currencyFormatter.format(this.costPerMonth / 100)
  }

  displayIncludedAmount() {
    this.includedAmountElement.textContent =
      this.includedAmount > 0 ? `${this.includedAmount.toString()} GiB` : '(choose a plan above)'
  }

  planUpdated(event: Event) {
    for (const planLabel of this.planLabels) {
      planLabel.classList.remove('gradient-border')
    }
    const plan = event.currentTarget as HTMLInputElement
    plan.closest('label')!.classList.add('gradient-border')
    this.displayIncludedAmount()
    this.calculate()
  }

  calculate() {
    const included = this.unit_data.hasOwnProperty(this.plan) ? this.unit_data[this.plan]!['value'] : 0
    const storageCharged = Math.max(this.storage - included, 0)
    const bandwidthCharged = Math.max(this.bandwidth - included, 0)
    const storageCost = storageCharged * this.unit_data.lfs_storage!.value
    const bandwidthCost = bandwidthCharged * this.unit_data.lfs_bandwidth!.value

    this.costPerMonth = storageCost + bandwidthCost
  }

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    this.bandwidthInput.value = ''
    this.storageInput.value = ''
    for (const plan of this.plans) {
      plan.checked = false
    }
    this.costPerMonth = 0
  }

  getSummaryForCSV() {
    // headers: ['Service', 'Configuration Summary', 'Notes', 'Cost Per Month']

    return [
      [
        'LFS',
        `"Storage (GiB): ${this.storage}, Bandwidth (GiB): ${this.bandwidth}, Included for free (GiB): ${this.includedAmount}"`,
        '',
        `"${currencyFormatter.format(this.costPerMonth / 100)}"`,
      ],
    ]
  }

  static observedAttributes = ['data-cost-per-month', 'data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return
    }

    if (name === 'data-cost-per-month' || name === 'data-active') {
      this.displayCostPerMonth()
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'lfsTotal', amount: this.costPerMonth, active: this.active},
        }),
      )

      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'lfs',
            resetEnabled: Boolean(this.storage > 0) || Boolean(this.bandwidth > 0),
          },
        }),
      )
    }
  }
}
